import React, { Fragment } from "react";

import { Tab } from "@headlessui/react";
import clsx from "clsx";

import Button from "components/button";
import Heading from "components/heading";
import HeroSection from "components/hero-section";

const stakeholders = [
  {
    title: "Financiers",
    features: [
      {
        name: "Real-time portfolio visibility",
        description:
          "Dashboards, drilldowns, and custom analytics across every funded solar deal — built for financiers who need more than a job count.",
      },
      {
        name: "Credit policy control",
        description:
          "Adjust decisioning rules, pricing guardrails, and approval criteria for your solar loan or TPO program without an engineering sprint.",
      },
      {
        name: "Dedicated Slack channel",
        description: "Direct line to Enerflo’s product and engineering team for escalations and feature requests.",
      },
      {
        name: "Audit-ready compliance reporting",
        description: "Full transaction-level data access via API or direct portal login.",
      },
      {
        name: "Co-marketing & installer webinars",
        description: "Joint installer campaigns and co-branded content to drive origination volume across the network.",
      },
      {
        name: "Dev hours covered",
        description:
          "No hourly charges for integration builds or feature development. Engineering resources included for Enerflo Native Financiers.",
      },
    ],
  },
  {
    title: "Sales Reps",
    features: [
      {
        name: "In-home rep support",
        description:
          "Sales reps get real-time support and guidance from within the operating system during the homeowner visit — keeping deals moving and reducing fall-off at point of sale.",
      },
      {
        name: "Instant accurate solar quotes",
        description:
          "Dynamic pricing engine generates per-customer solar financing pricing at the door — no spreadsheets or back-office calls.",
      },
      {
        name: "Pre-filled applications",
        description: "Customer data auto-populates the finance application from the project record — no double entry.",
      },
      {
        name: "Embedded signing",
        description:
          "Solar financing contracts sent and signed inside the operating system — no DocuSign handoff, no broken flow.",
      },
      {
        name: "Application status tracking",
        description:
          "Real-time visibility into where every solar deal stands in the lender’s approval process at any moment.",
      },
      {
        name: "Spanish language support",
        description: "Full bilingual solar contract and proposal flow for Spanish-speaking homeowners.",
      },
    ],
  },
  {
    title: "Installers",
    features: [
      {
        name: "Free access to your native financing product",
        description:
          "Installers get free access to Enerflo's origination and financing workflows — funded by the financier.",
      },
      {
        name: "Milestone tracking",
        description:
          "Submit project milestones and receive financier feedback without ever leaving the operating system.",
      },
      {
        name: "Change order management",
        description: "Modify post-signed solar contracts with automated re-sign workflows per financier rules.",
      },
      {
        name: "System detail auto-submission",
        description: "Solar system specs sent to the financier automatically on application — no manual data entry.",
      },
      {
        name: "Licensing management",
        description: "Contractor and rep license storage tied to origination permissions by state.",
      },
      {
        name: "Data insights app",
        description:
          "Job volume, funding rates, and performance at a glance — built for solar installation operations teams.",
      },
    ],
  },
  {
    title: "Homeowners",
    features: [
      {
        name: "Branded solar project portal",
        description: "Homeowners track their solar installation status under the financier’s brand — not Enerflo’s.",
      },
      {
        name: "Secure ID verification",
        description: "Live photo ID and selfie match at signing — consumer protection baked into the origination flow.",
      },
      {
        name: "Welcome call workflow",
        description:
          "Structured post-sign call process to confirm homeowner understanding and reduce solar loan cancellations.",
      },
      {
        name: "Financier communication",
        description:
          "Direct in-portal messaging between borrower and the solar financing team — coming in a future phase.",
        comingSoon: true,
      },
      {
        name: "Bill pay",
        description: "Solar loan payment management inside the homeowner portal — on the roadmap.",
        comingSoon: true,
      },
      {
        name: "System monitoring",
        description: "Post-PTO solar performance data surfaced to the borrower in their branded portal.",
        comingSoon: true,
      },
    ],
  },
] as const;

const title = "Solar Financing Platform — Built for Every Stakeholder";

const BuiltForStakeholders = () => {
  return (
    <div className="bg-navy">
      <HeroSection title={title}>
        <HeroSection.Content className="text-white space-y-10">
          <header>
            <HeroSection.Tagline className="text-primary mb-4">{title}</HeroSection.Tagline>
            <Heading tag="h2" className="mb-6">
              <span>One operating system.</span>{" "}
              <span className="text-primary">Every party in the solar financing deal.</span>
            </Heading>
            <p className="text-gray-300 max-w-4xl">
              Purpose-built experiences for every participant — so banks, credit unions, solar lenders, TPO financiers,
              installers, sales reps, and homeowners each have exactly what they need to move faster and fund more solar
              projects.
            </p>
          </header>

          <Tab.Group>
            <Tab.List className="flex gap-3 flex-wrap">
              {stakeholders.map(stakeholder => (
                <Tab key={stakeholder.title} as={Fragment}>
                  {({ selected }) => (
                    <Button
                      key={stakeholder.title}
                      variant={selected ? "solid" : "outline"}
                      color="white"
                      role="tab"
                      className={clsx(selected && "border-2 border-transparent")}
                    >
                      {stakeholder.title}
                    </Button>
                  )}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              {stakeholders.map(stakeholder => (
                <Tab.Panel
                  key={stakeholder.title}
                  as="ul"
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4 md:gap-y-6"
                >
                  {stakeholder.features.map(feature => (
                    <li
                      key={feature.name}
                      className="text-white space-y-2 px-4 py-3 rounded-2xl border-2 border-gray-400 bg-primary/20"
                    >
                      <Heading tag="h6">
                        {feature.name}
                        {feature.comingSoon && (
                          <span className="text-green-400 ml-2 text-sm px-1 py-0.5 rounded bg-green-700/50 font-bold uppercase">
                            Soon
                          </span>
                        )}
                      </Heading>
                      <p className="text-gray-200 text-sm">{feature.description}</p>
                    </li>
                  ))}
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>
        </HeroSection.Content>
      </HeroSection>
    </div>
  );
};

export default BuiltForStakeholders;
